import { computed, inject, Injectable } from '@angular/core';
import {
  BaseGridViewState,
  BASE_GRID_VIEW_STATE,
  BASE_LOAD_GRID,
} from '@data-access/extendables';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';

import { WcgFilterIndicatorsOptions } from '@shared/interfaces/wcg-table-utils.interface';

import { StudyGridTypes, StudyListSignalState } from '../types';
import { updateGridFilter } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class StudyListFacadeService extends BaseGridViewState {
  private readonly _studyGridStore =
    inject<StudyListSignalState>(BASE_GRID_VIEW_STATE);

  private readonly _studyTableUtils = inject(WcgTableUtilsService);
  private readonly _loadStudyGrid = inject(BASE_LOAD_GRID);

  readonly $studies = computed(() => this._studyGridStore.$studies());
  readonly $currentUrl = computed(() => this._studyGridStore.$currentUrl());

  /**
   * NOTE: We are going forward with using inject() instead of constructor injection
   * But Super class needs to have the parameters passed in constructor
   * which we cannot do in the parent class for this specific signal store.
   * Hence, we are using inject() here and passing the values to super()
   */
  constructor() {
    super();
  }

  /**
   * Reloads the study grid filtered by pin state, driven by the
   * All Studies / Pinned quick-filter chips on the Studies page.
   *
   * The current search term, sort and advanced filters are preserved so the
   * chip narrows the existing result set rather than resetting the grid;
   * pagination returns to the first page because the row count changes.
   *
   *
   * NOTE: currently NOT wired to the quick-filter chips — the pinned filter is
   * applied client-side in the grid component for now. Kept as the server-side
   * path for when the API filters on `is_pinned`.
   *
   * @param isPinned - `true` to show only the user's pinned studies, `false` for all accessible studies.
   * @example
   * ```ts
   * studyListFacade.updatePinnedFilter(true);
   * ```
   */
  updatePinnedFilter(
    type: StudyGridTypes,
    isPinned: boolean,
    entityId?: string,
  ): void {
    const _pageSize = this._studyGridStore.$pageView().pageSize ?? 25;
    this._studyGridStore.updatePageView({ page: 1, pageSize: _pageSize });

    const _params = this._studyTableUtils.generateTableQueryParams({
      ...this.$wcgTableValues(),
      page: 1,
    });

    this._studyGridStore.setByURL(type, entityId ?? '', isPinned);
    this._loadStudyGrid({ params: _params, updateColumns: false });
  }

  updateStudyGridFilter(filter: WcgFilterIndicatorsOptions) {
    const _constructFilter = updateGridFilter(
      filter,
      this._studyGridStore.$advancedFilters(),
    );
    this.updateGrid(
      'filter',
      {
        filters: _constructFilter.filters,
        page: this._studyGridStore.$pageView().page,
        pageSize: this._studyGridStore.$pageView().pageSize,
      },
      true,
      false,
    );
  }
}
